import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getClientWhatsApp,
  getWhatsAppStatus,
  linkWhatsAppGroup,
  listWhatsAppGroups,
  requestWhatsAppQr,
  startWhatsAppSync,
} from '../lib/whatsappApi';

// WhatsApp per client (opt-in module). Query keys share the 'whatsapp' prefix
// so a sync or a new link refreshes the Settings card and the client tab.
const WA = ['whatsapp'] as const;

/** Connection + QR + last sync. Polls faster while waiting for the QR scan. */
export function useWhatsAppStatus(enabled = true) {
  return useQuery({
    queryKey: [...WA, 'status'],
    queryFn: getWhatsAppStatus,
    enabled,
    refetchInterval: (q) => {
      const s = q.state.data;
      if (!s?.configured) return false;
      return s.connection?.connected ? 30_000 : 4_000;
    },
  });
}

export function useWhatsAppGroups(enabled = true) {
  return useQuery({ queryKey: [...WA, 'groups'], queryFn: listWhatsAppGroups, enabled });
}

export function useLinkWhatsAppGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; clientId: string | null }) =>
      linkWhatsAppGroup(input.groupId, input.clientId),
    onSuccess: () => qc.invalidateQueries({ queryKey: WA }),
  });
}

/** A new round of QR codes; the status poll then shows the first one. */
export function useRequestWhatsAppQr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: requestWhatsAppQr,
    onSuccess: () => {
      for (const ms of [500, 2_500]) {
        setTimeout(() => void qc.invalidateQueries({ queryKey: [...WA, 'status'] }), ms);
      }
    },
  });
}

export function useStartWhatsAppSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startWhatsAppSync,
    // The run is in the background; refresh soon and again a bit later.
    onSuccess: () => {
      for (const ms of [3_000, 15_000]) {
        setTimeout(() => void qc.invalidateQueries({ queryKey: WA }), ms);
      }
    },
  });
}

const PAGE = 100;

/** One client's conversation in a group, newest page first, then older ones. */
export function useClientWhatsApp(clientId: string | undefined, groupId: string | undefined) {
  return useInfiniteQuery({
    queryKey: [...WA, 'client', clientId, groupId ?? null],
    enabled: Boolean(clientId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      getClientWhatsApp(clientId as string, { groupId, before: pageParam, limit: PAGE }),
    getNextPageParam: (last) =>
      last.hasMore && last.messages.length > 0 ? last.messages[0].sentAt : undefined,
  });
}
